import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import toast from "react-hot-toast";
import { createCustomerTermChangeRequest } from "../../services/customer";
import {
  TERMS_REQUEST_SCOPES,
  TERMS_REQUEST_SCOPE_OPTIONS,
  buildTermsChangeRequestPayload,
} from "./termsChangeRequestHelpers";

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  "Unable to submit the terms change request.";

const TermsChangeRequestDialog = ({
  open,
  onClose,
  customerId,
  customerName,
  orderId,
  projectName,
  currentTermId,
  currentTermName,
  termsOptions = [],
  onSubmitted,
}) => {
  const [scope, setScope] = useState(TERMS_REQUEST_SCOPES.PROJECT_ONLY);
  const [requestedTermId, setRequestedTermId] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submitLockRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setScope(TERMS_REQUEST_SCOPES.PROJECT_ONLY);
    setRequestedTermId("");
    setReason("");
    setError("");
    setSubmitting(false);
    submitLockRef.current = false;
  }, [open]);

  const filteredTermsOptions = useMemo(() => {
    const currentId = String(currentTermId || "").trim();
    const options = Array.isArray(termsOptions) ? termsOptions : [];
    const filtered = options.filter((term) => String(term?.id || "").trim() !== currentId);
    return filtered.length ? filtered : options;
  }, [currentTermId, termsOptions]);

  const trimmedReason = String(reason || "").trim();
  const normalizedRequestedTermId = String(requestedTermId || "").trim();
  const isSameRequestedTerm =
    normalizedRequestedTermId &&
    String(currentTermId || "").trim() &&
    normalizedRequestedTermId === String(currentTermId || "").trim();

  const handleDialogClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleSubmit = async () => {
    if (submitting || submitLockRef.current) return;
    if (!normalizedRequestedTermId) {
      setError("Please select the requested terms.");
      return;
    }
    if (isSameRequestedTerm) {
      setError("Please select terms that differ from the current sales order terms.");
      return;
    }
    if (!trimmedReason) {
      setError("Please provide a reason for the terms request.");
      return;
    }

    submitLockRef.current = true;
    setSubmitting(true);
    setError("");
    try {
      const payload = buildTermsChangeRequestPayload({
        scope,
        requestedTermId: normalizedRequestedTermId,
        orderId,
        reason: trimmedReason,
      });
      const response = await createCustomerTermChangeRequest(customerId, payload);
      const warnings = [
        ...(Array.isArray(response?.data?.reviewerRouting?.warnings)
          ? response.data.reviewerRouting.warnings
          : []),
        ...(Array.isArray(response?.data?.notifications?.warnings)
          ? response.data.notifications.warnings
          : []),
      ].filter(Boolean);
      toast.success(
        warnings.length
          ? `Terms change request submitted for review. ${warnings[0]}`
          : "Terms change request submitted for review."
      );
      onSubmitted?.(response?.data?.request || { ...payload, status: "PENDING" });
      onClose?.();
    } catch (submitError) {
      setError(getErrorMessage(submitError));
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>Request Terms Change</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Box
            sx={{
              p: 1.5,
              borderRadius: "10px",
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Stack spacing={0.75}>
              <Typography variant="body2" fontWeight={700}>
                Customer
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {customerName || "Not available"}
              </Typography>

              <Typography variant="body2" fontWeight={700} sx={{ mt: 0.75 }}>
                Project
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {projectName || (orderId ? `Order #${orderId}` : "Not available")}
              </Typography>

              <Typography variant="body2" fontWeight={700} sx={{ mt: 0.75 }}>
                Current Sales Order Terms
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentTermName || "Not selected"}
              </Typography>
            </Stack>
          </Box>

          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, mb: 0.65, display: "block" }}>
              Apply change to
            </Typography>
            <Select
              fullWidth
              size="small"
              value={scope}
              onChange={(event) => setScope(event.target.value)}
              disabled={submitting}
            >
              {TERMS_REQUEST_SCOPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, mb: 0.65, display: "block" }}>
              Requested Terms
            </Typography>
            <Select
              fullWidth
              size="small"
              value={requestedTermId}
              onChange={(event) => setRequestedTermId(event.target.value)}
              disabled={submitting}
              displayEmpty
            >
              <MenuItem value="">
                <Typography sx={{ color: "text.disabled", fontSize: "0.84rem" }}>
                  Select requested terms
                </Typography>
              </MenuItem>
              {filteredTermsOptions.map((term) => (
                <MenuItem key={term.id} value={term.id}>
                  {term.name}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Reason for Request"
            placeholder="Explain why the customer or project requires different payment terms."
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            disabled={submitting}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose} disabled={submitting} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {submitting ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} color="inherit" />
              <span>Submitting...</span>
            </Stack>
          ) : (
            "Submit Request"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsChangeRequestDialog;
